export enum SurveyLogicScope {
  QUESTION = 'question',
  OPTION = 'option',
  EVENT_DISPATCH = 'event-dispatch',
}

export enum SurveyLogicActionType {
  MAKE_VISIBLE = 'make_visible',
  MAKE_INVISIBLE = 'make_invisible',
  MAKE_REQUIRED = 'make_required',
}

export type SurveyLogicControlType = 'option' | 'value';

export type SurveyLogicRuleType =
  | 'equals'
  | 'not_equals'
  | 'contains'
  | 'not_contains'
  | 'starts_with'
  | 'ends_with'
  | 'regex'
  | 'greater_than'
  | 'less_than'
  | 'greater_than_or_equal'
  | 'less_than_or_equal'
  | 'empty'
  | 'not_empty';

export type SurveyLogicOperator = 'and' | 'or';

export interface SurveyLogicTriggerRef {
  elementKey: string;
  triggerIndex: number;
}

export interface SurveyLogicTargetOptionKey {
  optionKey: string;
  questionKey: string;
}

export interface SurveyLogicTargetOptionKeyApi {
  option_key: string;
  question_key: string;
}

export interface SurveyLogicTargetStatementKey {
  statementKey: string;
  questionKey: string;
}

export interface SurveyLogicTargetStatementKeyApi {
  statement_key: string;
  question_key: string;
}

export interface SurveyLogicAction {
  type: SurveyLogicActionType;
  scope: SurveyLogicScope;
  targetElementKeys: string[];
  targetOptionKeys: SurveyLogicTargetOptionKey[];
  targetStatementKeys: SurveyLogicTargetStatementKey[];
  targetEvents: string[];
}

export interface SurveyLogicConditionSource {
  elementKey: string;
  triggerIndex: number;
}

export interface SurveyLogicCondition {
  id: string;
  source?: SurveyLogicConditionSource;
  triggerRef?: SurveyLogicTriggerRef;
  controlType: SurveyLogicControlType;
  ruleType: SurveyLogicRuleType;
  elementKey?: string;
  optionKey?: string;
  optionKeys?: string[];
  optionKeysOperator?: SurveyLogicOperator;
  answerValue?: string;
  statementKey?: string;
  statementKeys?: string[];
}

export interface SurveyLogicConditionGroup {
  id: string;
  operator: SurveyLogicOperator;
  conditions: SurveyLogicCondition[];
}

export interface SurveyLogicRule {
  id: string;
  name?: string;
  action: SurveyLogicAction;
  conditionGroups: SurveyLogicConditionGroup[];
  conditionGroupsOperator: SurveyLogicOperator;
}

export interface SurveyLogicLegacyTrigger {
  id?: string;
  scope: SurveyLogicScope;
  action: SurveyLogicActionType;
  controlType: SurveyLogicControlType;
  ruleType?: SurveyLogicRuleType;
  optionKey?: string;
  answerValue?: string;
  statementKey?: string;
  targetElementKey: string[];
  targetOptionKeys: SurveyLogicTargetOptionKey[];
  targetEvents: string[];
  targetStatementKeys: SurveyLogicTargetStatementKey[];
}

export interface SurveyLogicRulesConfig {
  id: string;
  name?: string;
  action: {
    type: SurveyLogicActionType;
    scope: SurveyLogicScope;
    target_element_keys?: string[];
    target_option_keys?: SurveyLogicTargetOptionKeyApi[];
    target_statement_keys?: SurveyLogicTargetStatementKeyApi[];
    target_events?: string[];
  };
  condition_groups: Array<{
    id: string;
    operator: SurveyLogicOperator;
    conditions: Array<{
      trigger_ref?: {
        element_key: string;
        trigger_index: number;
      };
      control_type?: SurveyLogicControlType;
      rule_type?: SurveyLogicRuleType;
      element_key?: string;
      option_key?: string;
      option_keys?: string[];
      option_keys_operator?: SurveyLogicOperator;
      answer_value?: string;
      statement_key?: string;
      statement_keys?: string[];
    }>;
  }>;
  condition_groups_operator: SurveyLogicOperator;
}

export interface SurveyLogicElement {
  id: string;
  key: string;
  stateId: string;
  stateKey: string;
  type: string;
  visible: boolean;
  body?: Record<string, string>;
  triggers?: SurveyLogicLegacyTrigger[];
  detail?: {
    id: string;
    style: string;
    body?: Record<string, string>;
    options?: SurveyLogicElementOption[];
    statements?: SurveyLogicElementStatement[];
    extra?: Record<string, unknown>;
  };
}

export interface SurveyLogicElementOption {
  id: string;
  key: string;
  body?: Record<string, string>;
  weight?: number;
}

export interface SurveyLogicElementStatement {
  id: string;
  key: string;
  body?: Record<string, string>;
  options?: SurveyLogicElementOption[];
}

export interface SurveyLogicState {
  id: string;
  key: string;
  name?: string;
  elements: SurveyLogicElement[];
}

export const SURVEY_LOGIC_AVAILABLE_EVENTS = ['redirect-to-store'] as const;

export type SurveyLogicAvailableEvent = (typeof SURVEY_LOGIC_AVAILABLE_EVENTS)[number];
